/**
 * Consolidated UseEndpoint Hook
 *
 * This hook consolidates all use_endpoint functionality into a single,
 * testable hook. It replaces the scattered context/utils/validation logic
 * with a clear, unified interface.
 *
 * @example
 * ```tsx
 * const { fetchEndpoint, isMissingForField, fetchState } = useEndpointFetch({
 *   interaction,
 *   submitRpc,
 *   showError
 * });
 *
 * // In a control:
 * const handleFetchClick = async () => {
 *   const result = await fetchEndpoint('get_beta', formData, 'beta');
 *   if (result.success) {
 *     handleChange(path, result.data.beta);
 *   }
 * };
 * ```
 */

import { useState, useCallback, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import type {
  UseEndpointFetchOptions,
  UseEndpointFetchResult,
  FetchState,
  FetchResult,
} from "./types";
import {
  getMissingPathsForEndpoint,
  getMissingPathsForRequiredKeys,
  getEndpointSchema,
  parseRequiredPropertiesFromError,
  parseRpcResultPayload,
  buildValidationMessage,
} from "./validation";
import { getErrorMessage } from "@/utils/getErrorMessage";

/**
 * Consolidated hook for use_endpoint functionality
 *
 * Manages:
 * - Fetching values from helper RPCs
 * - Validation of required fields
 * - Error handling and messaging
 * - Loading states
 * - Result caching
 */
export function useEndpointFetch({
  interaction,
  submitRpc,
  showError,
}: UseEndpointFetchOptions): UseEndpointFetchResult {
  const [fetchState, setFetchState] = useState<FetchState>({
    isLoading: false,
    missingPaths: [],
    endpointName: null,
    cache: {},
  });

  // Cache of required keys learned from previous validation errors
  const lastRequiredKeysByEndpoint = useRef<Record<string, string[]>>({});

  /**
   * Fetch a value from a use_endpoint RPC
   */
  const fetchEndpoint = useCallback(
    async (
      endpointName: string,
      formData: Record<string, unknown>,
      _propertyName: string
    ): Promise<FetchResult> => {
      // Start loading
      setFetchState((prev) => ({
        ...prev,
        isLoading: true,
      }));

      try {
        // Get endpoint schema from interaction
        const rawRpcs = (interaction as { rpcs?: any }).rpcs;
        const customRpcs = rawRpcs?.custom_rpcs;

        const endpointSchema = getEndpointSchema(
          customRpcs,
          endpointName
        ) as
          | { required?: string[]; properties?: Record<string, unknown> }
          | undefined;

        // Validate required fields
        if (endpointSchema) {
          const paths = getMissingPathsForEndpoint(formData, endpointSchema);
          if (paths.length > 0) {
            setFetchState((prev) => ({
              ...prev,
              isLoading: false,
              missingPaths: paths,
              endpointName,
            }));
            showError(buildValidationMessage(endpointName, paths));
            return { success: false, missingPaths: paths };
          }
        } else {
          // No schema available, check cached required keys from previous errors
          const cachedRequired =
            lastRequiredKeysByEndpoint.current[endpointName];
          if (cachedRequired?.length) {
            const paths = getMissingPathsForRequiredKeys(
              formData,
              cachedRequired
            );
            if (paths.length > 0) {
              setFetchState((prev) => ({
                ...prev,
                isLoading: false,
                missingPaths: paths,
                endpointName,
              }));
              showError(buildValidationMessage(endpointName, paths));
              return { success: false, missingPaths: paths };
            }
          }
        }

        // Clear previous validation errors
        setFetchState((prev) => ({
          ...prev,
          missingPaths: [],
          endpointName: null,
        }));

        // Make RPC request
        const body: Record<string, unknown> = {
          req_id: uuidv4(),
          connection_id: interaction.session_id,
          algo_id: interaction.algo_id,
          rpc_name: endpointName,
          args: formData,
        };

        // @ts-expect-error TS2571 - unwrap() return type from mutation hook is unknown
        const raw = await submitRpc({ data: body }).unwrap();
        const data = parseRpcResultPayload(raw) as Record<string, unknown>;

        // Update cache and clear loading
        setFetchState((prev) => ({
          ...prev,
          isLoading: false,
          cache: {
            ...prev.cache,
            [endpointName]: data,
          },
        }));

        return { success: true, data };
      } catch (err: unknown) {
        const message = getErrorMessage(err);
        showError(`Failed to fetch from "${endpointName}": ${message}`);

        // Try to extract required fields from error message
        const requiredFromError = parseRequiredPropertiesFromError(message);
        if (requiredFromError.length > 0) {
          lastRequiredKeysByEndpoint.current[endpointName] = requiredFromError;
          setFetchState((prev) => ({
            ...prev,
            isLoading: false,
            missingPaths: requiredFromError,
            endpointName,
          }));
          return {
            success: false,
            missingPaths: requiredFromError,
          };
        }

        setFetchState((prev) => ({
          ...prev,
          isLoading: false,
        }));
        return { success: false, missingPaths: [] };
      }
    },
    [interaction, submitRpc, showError]
  );

  /**
   * Check if a field path is missing for the current endpoint validation
   */
  const isMissingForField = useCallback(
    (fieldPath: string): boolean => {
      return fetchState.missingPaths.includes(fieldPath);
    },
    [fetchState.missingPaths]
  );

  return {
    fetchEndpoint,
    isMissingForField,
    fetchState,
  };
}
